/**
 * ログインページ (Phase 2.5 — real auth).
 *
 * email + password → POST /auth/login → Cookie セット → ダッシュボードへ遷移
 * ADR-0012 準拠。
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';
import { authLogin, authMagicLinkRequest, googleOAuthAuthorizeUrl } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';
import { getPendingInviteToken } from '../lib/invite-token.js';

export function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { oauth_error?: string };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);

  useEffect(() => {
    document.title = 'ログイン - PetaCMS';
    if (search?.oauth_error) {
      setError(
        search.oauth_error === 'state_mismatch'
          ? 'セキュリティ検証に失敗しました。もう一度お試しください。'
          : 'Google ログインに失敗しました。時間をおいて再度お試しください。',
      );
    }
  }, [search?.oauth_error]);

  const handleGoogleLogin = () => {
    window.location.href = googleOAuthAuthorizeUrl();
  };

  const handleMagicLink = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMagicLoading(true);
    try {
      await authMagicLinkRequest(magicEmail);
      setMagicSent(true);
    } catch (err) {
      setError(formatErrorMessage(err, 'ログインリンクの送信に失敗しました'));
    } finally {
      setMagicLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authLogin(email, password);
      const pendingInviteToken = getPendingInviteToken();
      if (pendingInviteToken) {
        navigate({ to: '/invite/accept', search: { token: pendingInviteToken } });
        return;
      }
      navigate({ to: '/sites' });
    } catch (err) {
      setError(formatErrorMessage(err, 'ログインに失敗しました。もう一度お試しください'));
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10">
        <div className="mb-8 text-center">
          <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>
          <p className="mt-1 text-[14px] text-[#706d65]">ペタッと貼るだけ。</p>
        </div>

        {error && (
          <div className="mb-5 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-sm text-[#991b1b]">
            {error}
          </div>
        )}

        {getPendingInviteToken() && (
          <div className="mb-5 rounded-[6px] border-l-4 border-[#3b82f6] bg-[#eff6ff] p-3 text-sm text-[#1e40af]">
            ログイン後に招待の参加処理を続けます。
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="mb-2 block text-[13px] font-medium text-[#23221e]">
              メールアドレス
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-2 block text-[13px] font-medium text-[#23221e]">
              パスワード
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
              className="h-[44px] w-full rounded-[6px] border border-[#d6d3d0] bg-white px-[14px] text-[14px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-[15px] font-semibold text-white transition hover:bg-[#1d4ed8] disabled:opacity-50"
          >
            {loading ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-[12px] text-[#a09d95]">
          <div className="h-px flex-1 bg-[#edebe8]" />
          <span>または</span>
          <div className="h-px flex-1 bg-[#edebe8]" />
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          className="flex h-[44px] w-full items-center justify-center gap-3 rounded-[6px] border border-[#d6d3d0] bg-white text-[14px] font-medium text-[#23221e] transition hover:bg-[#f8f7f6]"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path
              fill="#FFC107"
              d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
              fill="#FF3D00"
              d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
              fill="#4CAF50"
              d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
          </svg>
          Google でログイン
        </button>

        {magicSent ? (
          <div className="mt-4 rounded-[6px] border border-[#10b981] bg-[#ecfdf5] p-3 text-[13px] text-[#065f46]">
            ログインリンクを {magicEmail} 宛に送信しました。メールをご確認ください（15 分間有効）。
          </div>
        ) : (
          <form onSubmit={handleMagicLink} className="mt-4 space-y-2">
            <label htmlFor="magic-email" className="block text-[12px] font-medium text-[#706d65]">
              メールでログインリンクを受け取る
            </label>
            <div className="flex gap-2">
              <input
                id="magic-email"
                type="email"
                required
                value={magicEmail}
                onChange={(e) => setMagicEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={magicLoading}
                className="h-[40px] flex-1 rounded-[6px] border border-[#d6d3d0] bg-white px-[12px] text-[13px] text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/25"
              />
              <button
                type="submit"
                disabled={magicLoading}
                className="h-[40px] shrink-0 rounded-[6px] border border-[#3b82f6] bg-white px-4 text-[13px] font-semibold text-[#3b82f6] transition hover:bg-[#eff6ff] disabled:opacity-50"
              >
                {magicLoading ? '送信中…' : '送信'}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/forgot-password" className="text-[13px] text-[#706d65] hover:text-[#3b82f6]">
            パスワードをお忘れですか？
          </Link>
        </div>

        <div className="mt-5 text-center">
          <Link to="/register" className="text-[13px] text-[#3b82f6] hover:text-[#1d4ed8]">
            アカウントをお持ちでない方はこちら
          </Link>
        </div>
      </div>
    </div>
  );
}
